import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { languageAPI } from '../../utils/crmApi';
import {
    LanguageIcon,
    PlusIcon,
    EyeIcon,
    PencilIcon,
    TrashIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    GlobeAltIcon,
    LanguageIcon as TranslateIcon,
    DocumentTextIcon,
    UserGroupIcon,
    ChartBarIcon,
    Cog6ToothIcon,
    ArrowPathIcon,
    CloudIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useDialog, ConfirmationDialog } from '../../components/kibo-ui/dialog';
import {
    TableProvider,
    Table,
    TableHeader,
    TableSearch,
    TableHead,
    TableBody,
    TableRow,
    TableHeaderCell,
    TableCell,
    TableFooter,
    StatusBadge,
    TableAvatar
} from '../../components/kibo-ui/table';

const MultiLanguage = () => {
    const { openDialog } = useDialog();
    const [languages, setLanguages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [regionFilter, setRegionFilter] = useState('');
    const [pagination, setPagination] = useState({ page: 1, limit: 20 });
    const [selectedLanguages, setSelectedLanguages] = useState([]);
    const [bulkActionLoading, setBulkActionLoading] = useState(false);
    const [stats, setStats] = useState(null);
    const [activeTab, setActiveTab] = useState('languages');
    const [translations, setTranslations] = useState([]);
    const [translationKeys, setTranslationKeys] = useState([]);

    // Language status colors
    const getStatusColor = (status) => {
        const colors = {
            'active': 'bg-green-100 text-green-700',
            'inactive': 'bg-muted text-foreground',
            'pending': 'bg-yellow-100 text-yellow-700',
            'error': 'bg-red-100 text-red-700',
            'draft': 'bg-blue-100 text-blue-700'
        };
        return colors[status] || 'bg-muted text-foreground';
    };

    // Translation status colors
    const getTranslationStatusColor = (status) => {
        const colors = {
            'translated': 'bg-green-100 text-green-700',
            'pending': 'bg-yellow-100 text-yellow-700',
            'missing': 'bg-red-100 text-red-700',
            'needs_review': 'bg-orange-100 text-orange-700',
            'auto_translated': 'bg-blue-100 text-blue-700'
        };
        return colors[status] || 'bg-muted text-foreground';
    };

    // Language region icons
    const getRegionIcon = (region) => {
        const icons = {
            'europe': '🇪🇺',
            'americas': '🇺🇸',
            'asia': '🇨🇳',
            'africa': '🇿🇦',
            'oceania': '🇦🇺',
            'middle_east': '🇸🇦',
            'global': '🌍'
        };
        return icons[region] || '🌍';
    };

    useEffect(() => {
        loadLanguages();
        loadStats();
        loadTranslations();
        loadTranslationKeys();
    }, [search, statusFilter, regionFilter, pagination.page]);

    const loadLanguages = async () => {
        try {
            setLoading(true);
            const params = {
                page: pagination.page,
                limit: pagination.limit,
                ...(search && { search }),
                ...(statusFilter && { status: statusFilter }),
                ...(regionFilter && { region: regionFilter })
            };

            const response = await languageAPI.getAll(params);
            if (response.data.success) {
                setLanguages(response.data.data);
                if (response.data.pagination) {
                    setPagination(response.data.pagination);
                }
            }
        } catch (error) {
            console.error('Failed to load languages:', error);
            toast.error('Failed to load languages');
        } finally {
            setLoading(false);
        }
    };

    const loadStats = async () => {
        try {
            const response = await languageAPI.getStats();
            if (response.data.success) {
                setStats(response.data.data);
            }
        } catch (error) {
            console.error('Failed to load language stats:', error);
        }
    };

    const loadTranslations = async () => {
        try {
            const response = await languageAPI.getTranslations();
            if (response.data.success) {
                setTranslations(response.data.data);
            }
        } catch (error) {
            console.error('Failed to load translations:', error);
        }
    };

    const loadTranslationKeys = async () => {
        try {
            const response = await languageAPI.getTranslationKeys();
            if (response.data.success) {
                setTranslationKeys(response.data.data);
            }
        } catch (error) {
            console.error('Failed to load translation keys:', error);
        }
    };

    const handleSelectLanguage = (languageId) => {
        setSelectedLanguages(prev => 
            prev.includes(languageId) 
                ? prev.filter(id => id !== languageId)
                : [...prev, languageId]
        );
    };

    const handleSelectAll = () => {
        if (selectedLanguages.length === languages.length) {
            setSelectedLanguages([]);
        } else {
            setSelectedLanguages(languages.map(language => language._id));
        }
    };

    const handleBulkAction = (action) => {
        if (selectedLanguages.length === 0) return;
        
        const actionMessages = {
            'activate': 'activate',
            'deactivate': 'deactivate',
            'translate': 'translate',
            'delete': 'delete'
        };

        openDialog({
            title: `${actionMessages[action]} Languages`,
            description: `Are you sure you want to ${actionMessages[action]} ${selectedLanguages.length} language(s)?`,
            confirmText: actionMessages[action].charAt(0).toUpperCase() + actionMessages[action].slice(1),
            cancelText: 'Cancel',
            variant: action === 'delete' ? 'danger' : 'warning',
            onConfirm: async () => {
                try {
                    setBulkActionLoading(true);
                    await languageAPI.bulkAction(selectedLanguages, action);
                    toast.success(`${selectedLanguages.length} language(s) ${actionMessages[action]}ed successfully`);
                    setSelectedLanguages([]);
                    loadLanguages();
                } catch (error) {
                    console.error('Error performing bulk action:', error);
                    toast.error('Failed to perform bulk action');
                } finally {
                    setBulkActionLoading(false);
                }
            }
        });
    };

    const handleLanguageAction = async (languageId, action) => {
        try {
            await languageAPI.updateStatus(languageId, action);
            toast.success(`Language ${action}ed successfully!`);
            loadLanguages();
        } catch (error) {
            console.error(`Error ${action}ing language:`, error);
            toast.error(`Failed to ${action} language`);
        }
    };

    const handleDeleteLanguage = (languageId) => {
        openDialog({
            title: 'Delete Language',
            description: 'Are you sure you want to delete this language? This action cannot be undone.',
            confirmText: 'Delete Language',
            cancelText: 'Cancel',
            variant: 'danger',
            onConfirm: async () => {
                try {
                    await languageAPI.delete(languageId);
                    toast.success('Language deleted successfully');
                    loadLanguages();
                } catch (error) {
                    console.error('Error deleting language:', error);
                    toast.error('Failed to delete language');
                }
            }
        });
    };

    const handleAutoTranslate = async (languageId) => {
        try {
            await languageAPI.autoTranslate(languageId);
            toast.success('Auto-translation started successfully!');
            loadTranslations();
        } catch (error) {
            console.error('Error starting auto-translation:', error);
            toast.error('Failed to start auto-translation');
        }
    };

    const handleExportTranslations = async (languageId) => {
        try {
            const response = await languageAPI.exportTranslations(languageId);
            // Create blob and download
            const blob = new Blob([response.data]);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `translations-${languageId}.json`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error exporting translations:', error);
            toast.error('Failed to export translations');
        }
    };

    const columns = [
        { accessor: 'select', label: '', sortable: false, width: 'w-12' },
        { accessor: 'name', label: 'Language', sortable: true },
        { accessor: 'code', label: 'Code', sortable: true },
        { accessor: 'region', label: 'Region', sortable: true },
        { accessor: 'status', label: 'Status', sortable: true },
        { accessor: 'translationProgress', label: 'Translation Progress', sortable: true },
        { accessor: 'lastUpdated', label: 'Last Updated', sortable: true },
        { accessor: 'actions', label: 'Actions', sortable: false }
    ];

    const statusOptions = [
        { value: '', label: 'All Statuses' },
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
        { value: 'pending', label: 'Pending' },
        { value: 'error', label: 'Error' },
        { value: 'draft', label: 'Draft' }
    ];

    const regionOptions = [
        { value: '', label: 'All Regions' },
        { value: 'europe', label: 'Europe' },
        { value: 'americas', label: 'Americas' },
        { value: 'asia', label: 'Asia' },
        { value: 'africa', label: 'Africa' },
        { value: 'oceania', label: 'Oceania' },
        { value: 'middle_east', label: 'Middle East' },
        { value: 'global', label: 'Global' }
    ];

    const getActionButton = (language) => {
        switch (language.status) {
            case 'active':
                return (
                    <button
                        onClick={() => handleLanguageAction(language._id, 'deactivate')}
                        className="text-yellow-600 hover:text-yellow-800 text-sm font-medium"
                        title="Deactivate Language"
                    >
                        Deactivate
                    </button>
                );
            case 'inactive':
            case 'draft':
                return (
                    <button
                        onClick={() => handleLanguageAction(language._id, 'activate')}
                        className="text-green-600 hover:text-green-800 text-sm font-medium"
                        title="Activate Language"
                    >
                        Activate
                    </button>
                );
            default:
                return null;
        }
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatProgress = (progress) => {
        // Handle both decimal (0-1) and percentage (0-100) formats
        const percentage = progress > 1 ? Math.round(progress) : Math.round(progress * 100);
        return `${Math.min(Math.max(percentage, 0), 100)}%`;
    };

    const tabs = [
        { id: 'languages', name: 'Languages', icon: LanguageIcon },
        { id: 'translations', name: 'Translations', icon: TranslateIcon },
        { id: 'keys', name: 'Translation Keys', icon: DocumentTextIcon },
        { id: 'settings', name: 'Settings', icon: Cog6ToothIcon }
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-foreground">Multi-language Support</h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Manage translations and language support for international clients
                    </p>
                </div>
                <div className="mt-4 sm:mt-0 flex items-center gap-3">
                    {selectedLanguages.length > 0 && (
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => handleBulkAction('activate')}
                                disabled={bulkActionLoading}
                                className="inline-flex items-center px-3 py-2 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 disabled:opacity-50"
                            >
                                <CheckCircleIcon className="h-4 w-4 mr-1" />
                                Activate
                            </button>
                            <button
                                onClick={() => handleBulkAction('translate')}
                                disabled={bulkActionLoading}
                                className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 disabled:opacity-50"
                            >
                                <TranslateIcon className="h-4 w-4 mr-1" />
                                Translate
                            </button>
                            <button
                                onClick={() => handleBulkAction('delete')}
                                disabled={bulkActionLoading}
                                className="inline-flex items-center px-3 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50"
                            >
                                <TrashIcon className="h-4 w-4 mr-1" />
                                Delete
                            </button>
                        </div>
                    )}
                    <Link
                        to="/multi-language/new"
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring"
                    >
                        <PlusIcon className="h-5 w-5 mr-2" />
                        Add Language
                    </Link>
                </div>
            </div>

            {/* Tab Navigation */}
            <div className="border-b border-border overflow-x-auto overflow-y-hidden scrollbar-thin">
                <nav className="-mb-px flex space-x-4 sm:space-x-8 min-w-max sm:min-w-0">
                    {tabs.map((tab) => {
                        const Icon = tab.icon;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`${
                                    activeTab === tab.id
                                        ? 'border-primary text-primary'
                                        : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                                } whitespace-nowrap py-2 px-1 sm:px-1 border-b-2 font-medium text-xs sm:text-sm flex items-center flex-shrink-0`}
                            >
                                <Icon className="h-4 w-4 sm:h-5 sm:w-5 mr-1.5 sm:mr-2 flex-shrink-0" />
                                <span>{tab.name}</span>
                            </button>
                        );
                    })}
                </nav>
            </div>

            {/* Language Stats */}
            {activeTab === 'languages' && stats && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-card rounded-lg shadow-sm border border-border p-4">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <LanguageIcon className="h-8 w-8 text-blue-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-muted-foreground">Total Languages</p>
                                <p className="text-2xl font-semibold text-foreground">{stats.totalLanguages}</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-card rounded-lg shadow-sm border border-border p-4">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <CheckCircleIcon className="h-8 w-8 text-green-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-muted-foreground">Active Languages</p>
                                <p className="text-2xl font-semibold text-foreground">{stats.activeLanguages}</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-card rounded-lg shadow-sm border border-border p-4">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <TranslateIcon className="h-8 w-8 text-purple-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-muted-foreground">Translation Keys</p>
                                <p className="text-2xl font-semibold text-foreground">{stats.translationKeys}</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-card rounded-lg shadow-sm border border-border p-4">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <ChartBarIcon className="h-8 w-8 text-orange-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-muted-foreground">Avg Completion</p>
                                <p className="text-2xl font-semibold text-foreground">{formatProgress(stats.avgCompletion)}</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Filters */}
            <div className="bg-card rounded-lg shadow-sm border border-border p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Status</label>
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="w-full border border-input rounded-lg px-3 py-2 text-sm b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                        >
                            {statusOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Region</label>
                        <select
                            value={regionFilter}
                            onChange={(e) => setRegionFilter(e.target.value)}
                            className="w-full border border-input rounded-lg px-3 py-2 text-sm b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                        >
                            {regionOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="flex items-end">
                        <button
                            onClick={() => {
                                setStatusFilter('');
                                setRegionFilter('');
                                setSearch('');
                            }}
                            className="w-full px-4 py-2 text-sm font-medium text-foreground bg-muted border border-border rounded-lg hover:bg-muted/80"
                        >
                            Clear Filters
                        </button>
                    </div>
                </div>
            </div>

            {/* Enhanced Table with Kibo UI */}
            <TableProvider 
                data={languages} 
                columns={columns}
                onSort={(key, direction) => console.log('🔄 Table sort:', { key, direction })}
                onFilter={(key, value) => console.log('🔍 Table filter:', { key, value })}
            >
                <Table>
                    <TableHeader>
                        <TableSearch placeholder="Search languages by name, code, or region..." />
                    </TableHeader>
                    
                    <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
                        <div className="overflow-x-auto overflow-y-hidden scrollbar-thin -mx-1 sm:mx-0">
                            <table className="min-w-full divide-y divide-border">
                            <TableHead>
                                <tr>
                                    {columns.map((column) => (
                                        <TableHeaderCell key={column.accessor} column={column}>
                                            {column.accessor === 'select' ? (
                                                <input
                                                    type="checkbox"
                                                    checked={selectedLanguages.length === languages.length && languages.length > 0}
                                                    onChange={handleSelectAll}
                                                    className="h-4 w-4 text-primary focus:ring-primary border-input rounded"
                                                />
                                            ) : (
                                                column.label
                                            )}
                                        </TableHeaderCell>
                                    ))}
                                </tr>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={columns.length} className="px-6 py-12 text-center">
                                            <div className="flex items-center justify-center">
                                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                            </div>
                                        </td>
                                    </tr>
                                ) : languages.length === 0 ? (
                                    <tr>
                                        <td colSpan={columns.length} className="px-6 py-12 text-center">
                                            <div className="text-center">
                                                <LanguageIcon className="mx-auto h-12 w-12 text-muted-foreground" />
                                                <h3 className="mt-2 text-sm font-medium text-foreground">No languages found</h3>
                                                <p className="mt-1 text-sm text-muted-foreground">
                                                    Get started by adding your first language.
                                                </p>
                                                <Link
                                                    to="/multi-language/new"
                                                    className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                                                >
                                                    <PlusIcon className="h-5 w-5 mr-2" />
                                                    Add Language
                                                </Link>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    languages.map((language) => (
                                        <TableRow key={language._id}>
                                            <TableCell className="w-12">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedLanguages.includes(language._id)}
                                                    onChange={() => handleSelectLanguage(language._id)}
                                                    className="h-4 w-4 text-primary focus:ring-primary border-input rounded"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center">
                                                    <div className="text-2xl mr-3">{language.flag}</div>
                                                    <div>
                                                        <Link
                                                            to={`/multi-language/${language._id}`}
                                                            className="text-sm font-medium text-foreground hover:text-primary"
                                                        >
                                                            {language.name}
                                                        </Link>
                                                        <div className="text-xs text-muted-foreground">
                                                            {language.nativeName}
                                                        </div>
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/20 text-primary dark:bg-primary/30">
                                                    {language.code}
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center">
                                                    <span className="text-lg mr-2">{getRegionIcon(language.region)}</span>
                                                    <span className="text-sm text-foreground capitalize">
                                                        {language.region ? language.region.replace('_', ' ') : 'N/A'}
                                                    </span>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <StatusBadge 
                                                    status={language.status || 'unknown'}
                                                    className={getStatusColor(language.status)}
                                                >
                                                    {language.status ? language.status.charAt(0).toUpperCase() + language.status.slice(1) : 'Unknown'}
                                                </StatusBadge>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center">
                                                    <div className="w-full bg-muted rounded-full h-2 mr-2">
                                                        <div 
                                                            className="bg-primary h-2 rounded-full" 
                                                            style={{ width: `${(() => {
                                                                const progress = language.translationProgress || 0;
                                                                // Handle both decimal (0-1) and percentage (0-100) formats
                                                                const percentage = progress > 1 ? progress : progress * 100;
                                                                return Math.min(Math.max(percentage, 0), 100);
                                                            })()}%` }}
                                                        ></div>
                                                    </div>
                                                    <span className="text-sm text-foreground">
                                                        {formatProgress(language.translationProgress)}
                                                    </span>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="text-sm text-foreground">
                                                    {formatDate(language.lastUpdated)}
                                                </div>
                                                <div className="text-xs text-muted-foreground">
                                                    {language.lastUpdatedBy}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    {getActionButton(language)}
                                                    <button
                                                        onClick={() => handleAutoTranslate(language._id)}
                                                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                                                        title="Auto Translate"
                                                    >
                                                        <TranslateIcon className="h-4 w-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleExportTranslations(language._id)}
                                                        className="text-green-600 hover:text-green-800 text-sm font-medium"
                                                        title="Export Translations"
                                                    >
                                                        <CloudIcon className="h-4 w-4" />
                                                    </button>
                                                    <Link
                                                        to={`/multi-language/${language._id}`}
                                                        className="text-muted-foreground hover:text-foreground text-sm font-medium"
                                                        title="View"
                                                    >
                                                        <EyeIcon className="h-4 w-4" />
                                                    </Link>
                                                    <Link
                                                        to={`/multi-language/${language._id}/edit`}
                                                        className="text-muted-foreground hover:text-foreground text-sm font-medium"
                                                        title="Edit"
                                                    >
                                                        <PencilIcon className="h-4 w-4" />
                                                    </Link>
                                                    <button
                                                        onClick={() => handleDeleteLanguage(language._id)}
                                                        className="text-red-600 hover:text-red-800 text-sm font-medium"
                                                        title="Delete"
                                                    >
                                                        <TrashIcon className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                            </table>
                        </div>
                    </div>
                    
                    <TableFooter>
                        <div className="px-6 py-3 border-t border-border">
                            <div className="flex items-center justify-between">
                                <div className="text-sm text-muted-foreground">
                                    Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                                        disabled={pagination.page === 1}
                                        className="px-3 py-1 text-sm text-foreground bg-background border border-border rounded hover:bg-muted disabled:opacity-50"
                                    >
                                        Previous
                                    </button>
                                    <span className="px-3 py-1 text-sm text-muted-foreground">
                                        Page {pagination.page} of {pagination.pages}
                                    </span>
                                    <button
                                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                                        disabled={pagination.page === pagination.pages}
                                        className="px-3 py-1 text-sm text-foreground bg-background border border-border rounded hover:bg-muted disabled:opacity-50"
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        </div>
                    </TableFooter>
                </Table>
            </TableProvider>

            {/* Other tabs content would go here */}
            {activeTab !== 'languages' && (
                <div className="bg-card rounded-lg shadow-sm border border-border p-6">
                    <div className="text-center">
                        <div className="mx-auto h-12 w-12 text-muted-foreground">
                            {(() => {
                                const activeTabData = tabs.find(tab => tab.id === activeTab);
                                const IconComponent = activeTabData?.icon;
                                return IconComponent ? <IconComponent className="h-12 w-12" /> : null;
                            })()}
                        </div>
                        <h3 className="mt-2 text-sm font-medium text-foreground">
                            {tabs.find(tab => tab.id === activeTab)?.name}
                        </h3>
                        <p className="mt-1 text-sm text-muted-foreground">
                            This section will contain {activeTab} functionality.
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MultiLanguage;
